import { v4 } from "uuid";
import { useEffect, useState } from "react";
import { TFolder } from "../../models/folder-model";
import { filterFalsy } from "../../helpers/TsHelpers";
import { TFilesRetriever } from "../../services/files-retriever";
import { TFolderService } from "../../services/draft-attachments-service";
import { useNotificationStore } from "../Notifications/notification-store";
import {
  FolderExplorerPresentational,
  IFolderSelectedFileCount,
  TFolderSelectMode,
} from "./FolderExplorerPresentational";

interface TProps {
  matterId: string;
  allowRename?: boolean;
  allowCreate?: boolean;
  allowDelete?: boolean;
  selectMode: TFolderSelectMode;
  folderService: TFolderService;
  filesRetriever: TFilesRetriever;
  folderAndSelectedFileCounts?: IFolderSelectedFileCount;

  onFolderSelected?(folderId: string): void;
}

export const FolderExplorerContainer = ({
  matterId,
  allowRename,
  allowCreate,
  allowDelete,
  folderService,
  filesRetriever,
  selectMode = "SINGLE",
  folderAndSelectedFileCounts,
  onFolderSelected,
}: TProps) => {
  // Push / read errors
  const errorNotification = useNotificationStore((s) => s.errorNotification);

  // The id of the current folder we should be displaying for
  const [, setCurrentFolderId] = useState("");

  const [allFolders, setAllFolders] = useState<TFolder[]>([]);

  // used for creating new folders
  const [, setNewFolderName] = useState<string | null>(null);
  const [, setCreateFolderInProgress] = useState(false);

  const onSelectFolder = (folderId: string) => {
    setCurrentFolderId(folderId);

    if (onFolderSelected) {
      onFolderSelected(folderId);
    }
  };

  const getAllMatterFoldersAsync = async (matterId: string) => {
    let skip = 0;
    let hasData = true;
    const loadedFolders: TFolder[] = [];

    while (hasData) {
      const batch = await filesRetriever.getMatterFolders(matterId, { skip, take: 500 });

      if (batch.error) {
        console.error("Error loading matter folders: ", batch.error);
        errorNotification("Error loading folders", batch.error);

        hasData = false;
      } else {
        const paging = batch.paging;

        if (paging && paging.taken && paging.hasMore) {
          hasData = paging.hasMore;
          skip = skip + paging.taken;
        } else {
          hasData = false;
        }

        loadedFolders.push(...(batch.data || []));
      }
    }

    setAllFolders(loadedFolders.filter(filterFalsy));
  };

  useEffect(() => {
    getAllMatterFoldersAsync(matterId).catch((e) => errorNotification("Error loading folders", e));
  }, [matterId]);

  const onRenameFolder = (folderId: string, oldName: string, newName: string): void => {
    // TODO: Folder service needs to rename?

    const folder = allFolders.find((f) => f.id === folderId);
    if (folder) {
      folder.name = newName;
    }
  };

  const onDelete = (folderId: string): void => {
    // TODO: Folder service needs to delete?
    const folders = allFolders.filter((f) => f.id !== folderId);

    if (folders) {
      setAllFolders(folders);
    }
  };

  const onCreate = async (parentId: string | null, folderName: string): Promise<string> => {
    const newId = v4();

    if (!folderName) {
      folderName = "New Folder";
    }

    const parent = allFolders.find((folder) => folder.id === parentId);
    const depth = parent ? parent.depth + 1 : 1;

    const newFolder = {
      id: newId,
      depth: depth,
      subFolders: [],
      type: "FOLDER",
      matterId: matterId,
      parentId: parentId,
      dateCreated: new Date().toISOString(),
      dateModified: new Date().toISOString(),
      name: folderName ? folderName : "New Folder",
    } as TFolder;

    setCreateFolderInProgress(true);

    const success = await folderService.createNewFolder(folderName, newFolder.id, matterId, newFolder.parentId);

    if (!success) {
      errorNotification(
        "Could not create folder",
        "The server returned an error creating the folder. Please try again."
      );

      return "";
    }

    setCreateFolderInProgress(false);

    if (success) {
      const newFolders = [...allFolders];
      newFolders.push(newFolder);

      setAllFolders(newFolders);
      setCurrentFolderId(newId);
      setNewFolderName(null);
    }

    return newId;
  };

  return (
    <FolderExplorerPresentational
      matterId={matterId}
      folders={allFolders}
      selectMode={selectMode}
      allowRename={allowRename}
      allowCreate={allowCreate}
      allowDelete={allowDelete}
      folderAndSelectedFileCounts={folderAndSelectedFileCounts}
      onDelete={onDelete}
      onCreate={onCreate}
      onSelectFolder={onSelectFolder}
      onRenameFolder={onRenameFolder}
    />
  );
};
